import React, { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SidebarMenuItemType } from "../../../services/helpers/types";
import { setSidebarPositions } from "../../../services/edit/sidebarEdit.service";
import { useDispatch } from "react-redux";
import { deleteSidebarItem } from "../../../services/api/sidebar.api";
import {
  scrollElementToTop,
  scrollToElement,
} from "../../../services/helpers/commonHelpers";
import DraggableSidebarItem from "../DraggableSidebarItem/DraggableSidebarItem";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";

interface DraggableSidebarListProps {
  sidebarItems: SidebarMenuItemType[];
  currentSidebarItem: SidebarMenuItemType | undefined;
  onPageChange: (itemID?: number, shouldRefresh?: boolean) => void;
}

const DraggableSidebarList = (props: DraggableSidebarListProps) => {
  const { sidebarItems, currentSidebarItem, onPageChange } = props;

  const dispatch = useDispatch();

  const [items, setItems] = useState<number[]>([]);

  useEffect(() => {
    const sidebarItemIds = sidebarItems.map(
      (sidebarItem) => sidebarItem.sidebarItemID
    );
    setItems(sidebarItemIds);
  }, [sidebarItems]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor)
  );

  const handleChangePosition = (newItems: number[]) => {
    const newSidebarItems: SidebarMenuItemType[] = newItems.map(
      (item, index) => {
        const newItem = sidebarItems.find(
          (sidebarItem) => sidebarItem.sidebarItemID === item
        );
        if (!newItem) {
          return sidebarItems[index];
        }
        return { ...newItem, position: index + 1 };
      }
    );
    setSidebarPositions(dispatch, newSidebarItems);
  };

  const updatedItems = (items: number[], active: any, over: any) => {
    const oldIndex = items.indexOf(active.id);
    const newIndex = items.indexOf(over.id);

    return arrayMove(items, oldIndex, newIndex);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const newItems = updatedItems(items, active, over);

      setItems(newItems);
      handleChangePosition(newItems);
    }
  };

  const handleDelete = (itemID: number) => {
    const newItems = items.filter((id) => id !== itemID);

    setItems(newItems);
    deleteSidebarItem(dispatch, itemID).then((response) => {
      if (currentSidebarItem?.sidebarItemID === itemID) {
        if (newItems.length === 0) {
          onPageChange(undefined);
        }
        onPageChange(newItems[0]);
        scrollElementToTop("dui-sidebar-mid", "smooth");
      } else {
        scrollToElement("dui-sidebar-mid", "dui-sidebar-item-active");
      }
    });
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {items.map((id) => (
          <DraggableSidebarItem
            key={id}
            id={id}
            sidebarItem={sidebarItems.find((item) => item.sidebarItemID === id)}
            currentSidebarItem={currentSidebarItem}
            onPageChange={onPageChange}
            onDelete={handleDelete}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default DraggableSidebarList;
